/**
 * Global Cmd+K / Ctrl+K palette. Lives in the root layout so the keyboard
 * shortcut works on every page. Searches deals + RFPs + contacts; cmdk
 * does the fuzzy filtering client-side (it's fast enough for the row
 * counts we're at).
 *
 * Contacts are the exception: they are searched on the SERVER
 * (GET /contacts?q=&page=1, debounced). The unpaginated /contacts read stops
 * at 1000 rows, so a contact sorting past the cap could not be found here.
 *
 * Deliberately no Recents / Suggestions yet — the open list is just
 * `(deals ⨯ active) ∪ rfps ∪ contacts` flattened, ordered by recency of
 * stage_entered_at / deadline / last_touched_at respectively. When we
 * get into the thousands we'll add a server-side search endpoint and
 * debounce the input through it.
 */
import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { Briefcase, FileText, User } from 'lucide-react'
import { useApiGet } from '@/hooks/use-api'
import type { Deal, Rfp, Contact } from '@/types/domain'
import type { PagedResponse } from '@/lib/api-params'
import * as schemas from '@/schemas'

const CONTACT_RESULTS = 25

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])
  useEffect(() => { if (!open) setSearch('') }, [open])
  const navigate = useNavigate()

  // Only fetch once the palette is opened — keeps the cold-load cheap on
  // every page. React-Query caches the response so reopening is instant.
  const dealsQ    = useApiGet<Deal[]>('/deals',    { schema: schemas.dealList,    enabled: open })
  const rfpsQ     = useApiGet<Rfp[]>('/rfps',      { schema: schemas.rfpList,     enabled: open })
  const contactQs = new URLSearchParams({ page: '1', page_size: String(CONTACT_RESULTS) })
  if (debounced) contactQs.set('q', debounced)
  const contactsQ = useApiGet<PagedResponse<Contact>>(`/contacts?${contactQs.toString()}`, { enabled: open })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // metaKey covers ⌘ on Mac; ctrlKey covers Ctrl+K on Win/Linux. Most
      // browsers route Ctrl+K to the URL bar — preventDefault so the
      // palette wins. Empty input fields would otherwise also receive K.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const items = useMemo(() => {
    const deals = (dealsQ.data ?? []).filter((d) => !d.archived_at).map((d) => ({
      kind: 'deal' as const,
      id: d.id,
      label: d.name,
      sub: d.stage,
      // Straight to the deal's page. /pipeline?dealId= still works — Pipeline
      // redirects it — but going through that shim made every palette jump a
      // double navigation, and it left the test asserting on a URL that exists
      // only for the few milliseconds before the redirect lands.
      href: `/deals/${d.id}`,
    }))
    const rfps = (rfpsQ.data ?? []).map((r) => ({
      kind: 'rfp' as const,
      id: r.id,
      label: r.org_name,
      sub: `RFP · ${r.status}`,
      href: `/rfps/${r.id}`,
    }))
    const contacts = (contactsQ.data?.items ?? []).map((c) => ({
      kind: 'contact' as const,
      id: c.id,
      label: c.name,
      sub: [c.role, c.org].filter(Boolean).join(' · ') || 'Contact',
      // The server also matches email; keep it in cmdk's filter text so a
      // match on it is not filtered back out here.
      email: c.email ?? '',
      href: `/contacts/${c.id}`,
    }))
    return { deals, rfps, contacts }
  }, [dealsQ.data, rfpsQ.data, contactsQ.data])

  function jump(href: string) {
    setOpen(false)
    navigate(href)
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/30 pt-[15vh] backdrop-blur-[1px]"
      onClick={() => setOpen(false)}
    >
      <Command
        label="Global search"
        className="w-full max-w-[560px] overflow-hidden rounded-lg bg-white shadow-panel"
        onClick={(e) => e.stopPropagation()}
        // cmdk's default filter is case-insensitive prefix + substring;
        // ranks earlier matches higher. Good enough for the row counts
        // we're at without bringing in fuse.js.
      >
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Search deals, RFPs, contacts… (esc to close)"
          autoFocus
          className="w-full border-b border-slate-200 bg-transparent px-4 py-3 text-sm text-penser-ink placeholder:text-slate-400 focus:outline-none"
        />
        <Command.List className="max-h-[60vh] overflow-y-auto p-1">
          <Command.Empty className="px-3 py-6 text-center text-xs text-slate-400">
            No matches.
          </Command.Empty>

          {items.deals.length > 0 && (
            <Command.Group heading="Deals" className="text-[10px] uppercase tracking-wider text-slate-400 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
              {items.deals.map((d) => (
                <Command.Item
                  key={`deal-${d.id}`}
                  value={`${d.label} ${d.sub}`}
                  onSelect={() => jump(d.href)}
                  className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-slate-700 aria-selected:bg-penser-blue/10 aria-selected:text-penser-blue-dark"
                >
                  <Briefcase size={13} className="text-slate-400" />
                  <span className="flex-1 truncate">{d.label}</span>
                  <span className="text-[10px] text-slate-400">{d.sub}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {items.rfps.length > 0 && (
            <Command.Group heading="RFPs" className="text-[10px] uppercase tracking-wider text-slate-400 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
              {items.rfps.map((r) => (
                <Command.Item
                  key={`rfp-${r.id}`}
                  value={`${r.label} ${r.sub}`}
                  onSelect={() => jump(r.href)}
                  className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-slate-700 aria-selected:bg-penser-blue/10 aria-selected:text-penser-blue-dark"
                >
                  <FileText size={13} className="text-slate-400" />
                  <span className="flex-1 truncate">{r.label}</span>
                  <span className="text-[10px] text-slate-400">{r.sub}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {items.contacts.length > 0 && (
            <Command.Group heading="Contacts" className="text-[10px] uppercase tracking-wider text-slate-400 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
              {items.contacts.map((c) => (
                <Command.Item
                  key={`contact-${c.id}`}
                  value={`${c.label} ${c.sub} ${c.email}`}
                  onSelect={() => jump(c.href)}
                  className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-slate-700 aria-selected:bg-penser-blue/10 aria-selected:text-penser-blue-dark"
                >
                  <User size={13} className="text-slate-400" />
                  <span className="flex-1 truncate">{c.label}</span>
                  <span className="text-[10px] text-slate-400">{c.sub}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>
      </Command>
    </div>
  )
}
